import { Component, Injector, Input, OnInit } from '@angular/core';
import { WidgetResidualCalculationService } from '../../../../../services/WidgetResidualCalculationService';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * CapitalExpenditureCalculationOfDevelopmentCosts Widget
 */
@Component({
  selector: 'capital-expenditure-calculation-of-development-costs-widget',
  templateUrl: './capital-expenditure-calculation-of-development-costs-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './capital-expenditure-calculation-of-development-costs-widget.component.scss']
})
export class CapitalExpenditureCalculationOfDevelopmentCostsWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Reference list data
   */
  reflistDropDown = {};

  /**
   * Is widget readonly (obsolete?)
   */
  public readonly = false;

  /**
   * Versions Title Override (obsolete?)
   */
  public versionsTitleOverride = 'residualValue';

  /**
   * Report Designer Locale (obsolete?)
   */
  public reportDesignerLocale = null;

  /**
   * Version (obsolete?)
   */
  public version = null;

  /**
  * Constructor
  * @param widgetResidualCalculationService WidgetResidualCalculationService
  */
  constructor(injector: Injector, private widgetResidualCalculationService: WidgetResidualCalculationService) {
    super(injector, 'capitalExpenditureCalculationOfDevelopmentCosts', false);
    //override default translationName (which equals to name)
    this.translationName = 'residualValueCalculation';
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('saveChanges', () => {
      if (!this.isDirty) {
        this.saveChangesHandled();
      } else {
        this.widgetResidualCalculationService.save().then(() => {
          this.setDirtyFlag(false);
          this.saveChangesHandled();
        });
      }
    }));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.currentLayout = this.widgetConfig.widgetSettings.currentLayout;
    this.availableLayouts = this.widgetConfig.widgetSettings.availableLayouts;
    this.currentCalculation = this.currentCalculation || this.widgetConfig.widgetSettings.currentCalculation;
    this.availableCalculations = this.widgetConfig.widgetSettings.availableCalculations;

    this.mockup ?
      this.widgetResidualCalculationService.getMockupData().then((data: any) => {
        this.widgetData = this.preprocessLoadedData(data);
        this.onFieldChanged(null); //Re-calc mockup data
      })
      : this.widgetResidualCalculationService.load(this.getReport()).then((data: any) => {
        this.widgetData = this.preprocessLoadedData(data);
        this.page.widgetDataLoaded(this.widgetConfig.widgetSettings.id);
      });
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    this.setDirtyFlag(false);
    return data[this.currentCalculation];
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Save function - obsolete?
   */
  private save() {
    this.widgetResidualCalculationService.save();
  }

  /**
   * Calculation changed event handler
  * @param newCalculation {number} New Calculation
   */
  public calculationChanged(newCalculation: number): void {
    try {
      if (this.widgetConfig?.widgetSettings?.currentCalculation) {
        this.widgetConfig.widgetSettings.currentCalculation = newCalculation;
        this.currentCalculation = newCalculation;

        this.widgetData = null;
        setTimeout(() => {
          this.mockup ?
            this.widgetResidualCalculationService.getMockupData().then((data: any) => {
              this.widgetData = this.preprocessLoadedData(data);
            })
            : this.widgetResidualCalculationService.load(this.getReport()).then((data: any) => {
              this.widgetData = this.preprocessLoadedData(data);
            });
          this.setDirtyFlag(false);
        });
      }
    } catch (e) {
      // Nothing
    }
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(newLayout: string): void {
    try {
      if (this.widgetConfig?.widgetSettings?.layoutSizeConfiguration) {
        const config = this.widgetConfig.widgetSettings.layoutSizeConfiguration[newLayout];
        if (config.x) {
          this.widgetConfig.minSizeX = config.x;
          this.widgetConfig.sizeX = config.x;

        }
        if (config.y) {
          this.widgetConfig.sizeY = config.y;
        }
      }
      this.widgetConfig.widgetSettings.currentLayout = newLayout;
    } catch (e) {
      // Nothing
    }
    if (this.widgetData) {
      this.widgetData.WidgetLayout = newLayout;
      this.onFieldChanged(null);
    }
  }

  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param _index {number} Index
   * @param el {any} Element
   */
  public trackById(_index: number, el: any): number {
    return el.trackId;
  }

  /**
   * CapitalExpenditureCalculationOfDevelopmentCosts Add clicked event handler
   */
  public capitalExpenditureCalculationOfDevelopmentCostsAddClicked() {
    const row = {
      Oid: -1,
      ReportId: this.getReport(),
      Description: "",
      GrossBuildingAreaM2: 0,
      UnitsQuantity: 0,
      CostsM2: 0,
      CostsTotal: 0,
      VatDamage: 0
    };

    // add to observable array
    this.widgetData.ConstructionInstallations.push(row);
    this.setDirtyFlag(true);
    this.recalcTotalConstructionCostsTogetherCalc();
  }

  /**
   * CapitalExpenditureCalculationOfDevelopmentCosts300 Add clicked event handler
   */
  public capitalExpenditureCalculationOfDevelopmentCosts300AddClicked() {
    const row = {
      Oid: -1,
      ReportId: this.getReport(),
      Description: "",
      GrossBuildingAreaM2: 0,
      UnitsQuantity: 0,
      CostsM2: 0,
      CostsTotal: 0,
      VatDamage: 0
    };

    // add to observable array
    this.widgetData.ConstructionInstallations300.push(row);
    this.setDirtyFlag(true);
    this.recalcTotalConstructionCostsSeparatelyCalc();
  }

  /**
   * CapitalExpenditureCalculationOfDevelopmentCosts400 Add clicked event handler
   */
  public capitalExpenditureCalculationOfDevelopmentCosts400AddClicked() {
    const row = {
      Oid: -1,
      ReportId: this.getReport(),
      Description: "",
      GrossBuildingAreaM2: 0,
      UnitsQuantity: 0,
      CostsM2: 0,
      CostsTotal: 0,
      VatDamage: 0
    };

    // add to observable array
    this.widgetData.ConstructionInstallations400.push(row);
    this.setDirtyFlag(true);
    this.recalcTotalConstructionCostsSeparatelyCalc();
  }

  /**
   * CapitalExpenditureCalculationOfDevelopmentCostsSiteServices Add clicked event handler
   */
  public capitalExpenditureCalculationOfDevelopmentCostsSiteServicesAddClicked() {
    const row = {
      Oid: -1,
      ReportId: this.getReport(),
      Description: "",
      Costs: 0
    };

    // add to observable array
    this.widgetData.ArrangingPreparationSiteServices.push(row);
    this.setDirtyFlag(true);
    this.onFieldChanged(null);
  }

  /**
   * CapitalExpenditureCalculationOfDevelopmentCosts Delete clicked event handler
   */
  public capitalExpenditureCalculationOfDevelopmentCostsDeleteClicked() {
    const checkedRows = this.widgetData.ConstructionInstallations.filter((val) => {
      return val.checked;
    });
    checkedRows.forEach(row => {
      this.widgetData.ConstructionInstallations = this.widgetData.ConstructionInstallations.filter((item) => {
        return item !== row
      });
    });
    this.setDirtyFlag(true);
    this.onFieldChanged(null);
  }

  /**
   * CapitalExpenditureCalculationOfDevelopmentCosts300 Delete clicked event handler
   */
  public capitalExpenditureCalculationOfDevelopmentCosts300DeleteClicked() {
    const checkedRows = this.widgetData.ConstructionInstallations300.filter((val) => {
      return val.checked;
    });
    checkedRows.forEach(row => {
      this.widgetData.ConstructionInstallations300 = this.widgetData.ConstructionInstallations300.filter((item) => {
        return item !== row
      });
    });
    this.setDirtyFlag(true);
    this.onFieldChanged(null);
  }

  /**
   * CapitalExpenditureCalculationOfDevelopmentCosts400 Delete clicked event handler
   */
  public capitalExpenditureCalculationOfDevelopmentCosts400DeleteClicked() {
    const checkedRows = this.widgetData.ConstructionInstallations400.filter((val) => {
      return val.checked;
    });
    checkedRows.forEach(row => {
      this.widgetData.ConstructionInstallations400 = this.widgetData.ConstructionInstallations400.filter((item) => {
        return item !== row
      });
    });
    this.setDirtyFlag(true);
    this.recalcTotalConstructionCostsSeparatelyCalc();
  }

  /**
   * CapitalExpenditureCalculationOfDevelopmentCostsSiteServices Delete clicked event handler
   */
  public capitalExpenditureCalculationOfDevelopmentCostsSiteServicesDeleteClicked() {
    const checkedRows = this.widgetData.ArrangingPreparationSiteServices.filter((val) => {
      return val.checked;
    });
    checkedRows.forEach(row => {
      this.widgetData.ArrangingPreparationSiteServices = this.widgetData.ArrangingPreparationSiteServices.filter((item) => {
        return item !== row
      });
    });
    this.setDirtyFlag(true);
    this.onFieldChanged(null);
  }

  /**
   * Field changed event handler
   */
  public onFieldChanged(changedFieldName) {
    this.widgetData = this.widgetResidualCalculationService.calculate(this, changedFieldName);
  }

  /**
   * OutdoorFacilitiesPercentage changed event handler
   */
  public outdoorFacilitiesPercentageChanged() {
    this.setDirtyFlag(true);
    const val = this.widgetData.OutdoorFacilitiesPercentage;
    const total = this.widgetData.TotalConstructionCostsCalc;

    this.widgetData.OutdoorFacilitiesValue = 0;
    try {
      const calc = ((val / 100) * total);
      this.widgetData.OutdoorFacilitiesValue = calc;
    } catch (e) {
      //ignore
    }

    this.onFieldChanged(null);
  }

  /**
   * OutdoorFacilitiesValue changed event handler
   */
  public outdoorFacilitiesValueChanged() {
    this.setDirtyFlag(true);
    const val = this.widgetData.OutdoorFacilitiesValue;
    const total = this.widgetData.TotalConstructionCostsCalc;

    this.widgetData.OutdoorFacilitiesPercentage = 0;
    try {
      const calc = ((val / total) * 100);
      this.widgetData.OutdoorFacilitiesPercentage = calc;
    } catch (e) {
      //ignore
    }

    this.onFieldChanged(null);
  }

  /**
   * GrossBuildingAreaM2 changed event handler
   */
  public grossBuildingAreaM2Changed(entry) {
    this.setDirtyFlag(true);
    const val = entry.CostsM2;

    entry.CostsTotal = "";
    try {
      if (entry.GrossBuildingAreaM2) {
        entry.CostsTotal = entry.GrossBuildingAreaM2 * val;
      }
      else if (entry.UnitsQuantity) {
        entry.CostsTotal = entry.UnitsQuantity * val;
      }
      else {
        entry.CostsTotal = '';
      }
    } catch (e) {
      //ignore
    }

    this.onFieldChanged(null);
  }

  /**
   * UnitsQuantity changed event handler
   */
  public unitsQuantityChanged(entry) {
    this.setDirtyFlag(true);
    const val = entry.CostsM2;
    try {

      if (entry.UnitsQuantityChanged) {
        entry.CostsTotal = entry.UnitsQuantityChanged * val;
      }
      else if (entry.GrossBuildingAreaM2) {
        entry.CostsTotal = entry.GrossBuildingAreaM2 * val;
      }
      else {
        entry.CostsTotal = '';
      }
    } catch (e) {
      //ignore
    }

    this.onFieldChanged(null);
  }

  /**
   * ConstructionInstallationsCostsM2 changed event handler
   */
  public constructionInstallationsCostsM2Changed(entry) {
    this.grossBuildingAreaM2Changed(entry);
  }

  /**
   * ConstructionInstallationsCostsTotal changed event handler
   */
  public constructionInstallationsCostsTotalChanged(entry) {
    this.setDirtyFlag(true);
    const val = entry.CostsTotal;

    try {
      if (entry.GrossBuildingAreaM2) {
        entry.CostsM2 = val / entry.GrossBuildingAreaM2;
      }
      else if (entry.UnitsQuantity) {
        entry.CostsM2 = val / entry.UnitsQuantity;
      }
      else {
        entry.CostsM2 = '';
      }
    } catch (e) {
      //ignore
    }

    this.onFieldChanged(null);
  }

  /**
   * Recalculate Total Construction Costs separately
   */
  public recalcTotalConstructionCostsSeparatelyCalc() {
    this.widgetData.TotalGBACalcConstructionInstallations300 = this.widgetData.ConstructionInstallations300.reduce((a, b) => a + (b.GrossBuildingAreaM2 || 0), 0);
    this.widgetData.CostsTotalCalcConstructionInstallations300 = this.widgetData.ConstructionInstallations300.reduce((a, b) => a + (b.CostsTotal || 0), 0);
    this.widgetData.TotalGBACalcConstructionInstallations400 = this.widgetData.ConstructionInstallations400.reduce((a, b) => a + (b.GrossBuildingAreaM2 || 0), 0);
    this.widgetData.CostsTotalCalcConstructionInstallations400 = this.widgetData.ConstructionInstallations400.reduce((a, b) => a + (b.CostsTotal || 0), 0);

    this.widgetData.TotalConstructionCostsCalcSeparately = this.widgetData.ConstructionInstallations300.reduce((a, b) => a + (b.CostsTotal || 0), 0) + this.widgetData.ConstructionInstallations400.reduce((a, b) => a + (b.CostsTotal || 0), 0);
  }

  /**
   * Recalculate Total Construction Costs together
   */
  public recalcTotalConstructionCostsTogetherCalc() {
    this.widgetData.TotalGBACalcConstructionInstallations = this.widgetData.ConstructionInstallations300.reduce((a, b) => a + (b.GrossBuildingAreaM2 || 0), 0);
    this.widgetData.CostsTotalCalcConstructionInstallations = this.widgetData.ConstructionInstallations300.reduce((a, b) => a + (b.CostsTotal || 0), 0);

    this.widgetData.TotalConstructionCostsCalcTogether = this.widgetData.ConstructionInstallations.reduce((a, b) => a + (b.CostsTotal || 0), 0);
  }

  /**
   * Calculate AssumedStartOfConstruction
   */
  public calculateAssumedStartOfConstruction() {
    const date = new Date(this.widgetData.ValuationDate);
    const day = date.getDate();
    date.setMonth(date.getMonth() + this.widgetData.PlanningPeriod);
    if (date.getDate() != day) {
      date.setDate(0);
    }
    this.widgetData.AssumedStartOfConstruction = date;
  }

  /**
   * Calculate AssumedCompletion
   */
  public calculateAssumedCompletion() {
    const date = new Date(this.widgetData.AssumedStartOfConstruction);
    const day = date.getDate();
    date.setMonth(date.getMonth() + this.widgetData.ConstructionMarketing);
    if (date.getDate() != day) {
      date.setDate(0);
    }
    this.widgetData.AssumedCompletion = date;
  }

  /**
   * Change current widget layout
  * @param event Event object
   */
  private changeCurrentLayout(event: any): void {
    this.currentLayout = event;
  }

}
